import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";

import Grid from "@mui/material/Grid";
import LoadingButton from "@mui/lab/LoadingButton";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import { Box, InputLabel, OutlinedInput } from "@mui/material";

import Title from "../../../common/Title";
import useAuth from "../../../../hooks/useAuth";

const PlanForm = ({ isUpdate, handleSubmit, initialValues, loading }) => {
  const { user } = useAuth();
  const nav = useNavigate();

  const initialData = {
    plan_id: "",
    plan_name: "",
  };

  if (!user.admin) {
    nav("/");
  }

  const [formData, setFormData] = useState(null);

  const validationSchema = yup.object({
    plan_id: yup.string("plan_id").required("Plan ID is required"),
    plan_name: yup.string("plan_name").required("Plan name is required"),
  });

  const formik = useFormik({
    initialValues:
      initialValues && Object.keys(initialValues).length
        ? { ...initialData, ...initialValues }
        : { ...initialData },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      setFormData({
        plan_id: values.plan_id,
        plan_name: values.plan_name,
      });
    },
    validateOnChange: true,
  });

  useEffect(() => {
    if (formData) {
      handleSubmit(formData);
      if (!loading) {
        formik.setSubmitting(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  if (loading) {
    return <Title content="Please wait..." variant="subtitle2" gutterBottom />;
  }

  return (
    <>
      <Box
        sx={{ width: "100%", mb: 5 }}
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        <Title
          variant="h5"
          content={`${isUpdate ? "Update" : "Create"} PWG Plan`}
          sx={{ mb: 3 }}
          gutterBottom
        />
        <Grid
          sx={{ flexGrow: 1 }}
          container
          spacing={2}
          direction="column"
          justifyContent="center"
          alignItems="center"
          maxWidth={350}
        >
          <Grid item xs={12} style={{ width: 350 }}>
            <FormControl
              error={
                formik.touched.plan_id && Boolean(formik.errors.plan_id)
                  ? true
                  : false
              }
              sx={{ width: "100%" }}
            >
              <InputLabel htmlFor="plan_id">
                Plan ID (from PWG Onboarding Profile)
              </InputLabel>
              <OutlinedInput
                name="plan_id"
                label="Plan ID (from PWG Onboarding Profile)"
                id="plan_id"
                value={formik.values.plan_id}
                onChange={formik.handleChange}
              />
              {formik.touched.plan_id && Boolean(formik.errors.plan_id) ? (
                <FormHelperText id="plan_id-error-text">
                  {formik.errors.plan_id}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} style={{ width: 350 }}>
            <FormControl
              error={
                formik.touched.plan_name && Boolean(formik.errors.plan_name)
                  ? true
                  : false
              }
              sx={{ width: "100%" }}
            >
              <InputLabel htmlFor="plan_name">Display Name</InputLabel>
              <OutlinedInput
                name="plan_name"
                label="Display Name"
                id="plan_name"
                value={formik.values.plan_name}
                onChange={formik.handleChange}
              />
              {formik.touched.plan_name && Boolean(formik.errors.plan_name) ? (
                <FormHelperText id="plan_name-error-text">
                  {formik.errors.plan_name}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid
            container
            sx={{ mt: 3 }}
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
          >
            <Grid item>
              <LoadingButton
                style={{ marginRight: "5px" }}
                disabled={Object.keys(formik.errors).length}
                onClick={() =>
                  !Object.keys(formik.errors).length &&
                  !loading &&
                  !formik.isSubmitting &&
                  formik.submitForm()
                }
                size="large"
                loading={loading}
                variant="contained"
              >
                {isUpdate ? "Update" : "Create"}
              </LoadingButton>
            </Grid>
            <Grid item>
              <LoadingButton
                style={{ marginRight: "5px" }}
                size="large"
                onClick={() => nav("/pwg/plan")}
                loading={loading}
                variant="contained"
              >
                Cancel
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default PlanForm;
